import { format, parseISO } from "date-fns";
import type { Locale } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";

/**
 * Display variants for read-only dates. Add a member here (and a matching token
 * below) to introduce a new style without touching call sites.
 *
 * Implemented as a const object + union type (instead of a TS `enum`) so it
 * stays valid under `erasableSyntaxOnly`, while still allowing
 * `DateFormatVariant.DateTime`-style usage at call sites.
 */
export const DateFormatVariant = {
  Date: "date",
  Time: "time",
  DateTime: "dateTime",
  DateTimeMedium: "dateTimeMedium",
} as const;

export type DateFormatVariant = (typeof DateFormatVariant)[keyof typeof DateFormatVariant];

export const DATE_FORMAT_TOKENS: Record<DateFormatVariant, string> = {
  [DateFormatVariant.Date]: "P", // 08/07/2026
  [DateFormatVariant.Time]: "p", // 21:27
  [DateFormatVariant.DateTime]: "P p", // 08/07/2026 21:27
  [DateFormatVariant.DateTimeMedium]: "PP p", // Jul 8, 2026 21:27
};

/** Resolve the date-fns locale from an i18n language code. */
export function resolveDateLocale(language: string | undefined | null): Locale {
  return (language ?? "").toLowerCase().startsWith("pt") ? ptBR : enUS;
}

function toDate(value: string | number | Date): Date {
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  return parseISO(value);
}

/**
 * Format a date/datetime for read-only display. Accepts an ISO string (UTC or
 * local), epoch millis, or a Date. Returns an empty string for nullish input
 * and echoes the raw string when it cannot be parsed.
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  locale: Locale,
  variant: DateFormatVariant = DateFormatVariant.DateTime,
): string {
  if (value == null || value === "") return "";
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return typeof value === "string" ? value : "";
  return format(date, DATE_FORMAT_TOKENS[variant], { locale });
}
